import { memo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type Color } from '@air/api/types';

import { type ColorFilterProps } from '~/components/Filters/ColorFilter/ColorFilter';
import {
  ColorThresholdControl,
  type ColorThresholdControlProps,
} from '~/components/Filters/ColorFilter/components/ColorThresholdControl';
import { CustomColorPickerControl } from '~/components/Filters/ColorFilter/components/CustomColorPickerControl';
import { DefaultColorFilterControl } from '~/components/Filters/ColorFilter/components/DefaultColorPickerControl';
import { type BaseFilterCardProps, FilterCard } from '~/components/Filters/FilterCard';
import {
  addColorFilterValueAction,
  addColorRangeFilterValueAction,
  clearColorFilterValuesAction,
  removeColorFilterValueAction,
  removeFilterAction,
} from '~/store/filters/actions';
import { selectedColorsFiltersSelector } from '~/store/filters/selectors';
import { type SelectedFilter } from '~/store/filters/types';

export type ColorToggleParams = {
  color: Color;
  isSelected: boolean;
};

export type ColorFilterCardProps = {
  colors: ColorFilterProps['colors'];
} & BaseFilterCardProps;

export const ColorFilterCard = memo(({ colors, ...props }: ColorFilterCardProps) => {
  const dispatch = useDispatch();
  const selectedColors = useSelector(selectedColorsFiltersSelector);
  const customColorPickerRef = useRef<{ clearSavedHexes: () => void }>(null);
  const clearValues = useCallback(() => {
    dispatch(clearColorFilterValuesAction());
    if (customColorPickerRef.current) {
      customColorPickerRef.current.clearSavedHexes();
    }
  }, [dispatch]);

  const handleColorToggle = useCallback(
    ({ color, isSelected }: ColorToggleParams) => {
      if (isSelected) {
        dispatch(addColorFilterValueAction({ color: color.hex }));
      } else {
        dispatch(removeColorFilterValueAction({ color: color.hex }));
      }
    },
    [dispatch],
  );

  const onColorRangeCommit = useCallback<Required<ColorThresholdControlProps>['onThresholdChange']>(
    (value) => {
      dispatch(addColorRangeFilterValueAction(value));
    },
    [dispatch],
  );

  const onRemoveFilter = useCallback(
    (filter: SelectedFilter) => {
      dispatch(removeFilterAction({ filter }));
      clearValues();
    },
    [dispatch, clearValues],
  );

  const onRemoveHex = useCallback(
    (hex: string) => {
      dispatch(removeColorFilterValueAction({ color: hex }));
    },
    [dispatch],
  );

  return (
    <FilterCard currentFilter="color" onClearClick={clearValues} onRemoveFilter={onRemoveFilter} {...props}>
      <div className="flex flex-col gap-4">
        <DefaultColorFilterControl colors={colors} onClick={handleColorToggle} selectedColors={selectedColors.colors} />
        <CustomColorPickerControl
          colors={colors}
          ref={customColorPickerRef}
          onColorSelect={handleColorToggle}
          onRemoveHex={onRemoveHex}
          selectedColors={selectedColors.colors}
        />
        <ColorThresholdControl
          defaultValue={selectedColors.threshold ? [selectedColors.threshold] : undefined}
          onThresholdChange={onColorRangeCommit}
        />
      </div>
    </FilterCard>
  );
});

ColorFilterCard.displayName = 'ColorFilterCard';
